import {
    TaxFilingStatus,
    TAX_FILING_SINGLE,
    calculateTaxableGainsFromSyndication,
    calculateTaxWithStandardDeduction,
} from './taxes';
import { Syndication, PortfolioYear } from './types/syndication';
import { calculateNetGainForSyndicationsThisYear, calculateYearlyDistributionsForSyndications, getSyndicationsWithCarriedLoss } from './utils/syndication';
import { calculateDepreciationForYear, calculateTotalDepreciation } from './utils/syndication';

export const YEARS_TO_MATURITY = 5;
export const YEARS = 20;
const STARTING_YEAR = new Date().getFullYear();

// flag saying whether or not we should pay last year's syndication taxes with equity
const SHOULD_PAY_TAXES_WITH_EQUITY = false;

export const COMPARISON_CASH_COMES_FROM_LIQUIDATION = 'LIQUIDATION';
export const COMPARISON_CASH_COMES_FROM_CASH_FLOW = 'CASH_FLOW';
export type ComparisonCashComesFrom = typeof COMPARISON_CASH_COMES_FROM_CASH_FLOW | typeof COMPARISON_CASH_COMES_FROM_LIQUIDATION;

interface ComparisonInvestment {
    returnRate: number;
    liquidationRate: number;
    cashDistributionsComeFrom: ComparisonCashComesFrom;
}

interface PortfolioParameters {
    investPerYear: number;
    cocReturn: number;
    equityMultiple: number;
    stopInvestingAtYear: number;
    stopReinvestingAtYear: number;
    comparisonInvestment: ComparisonInvestment
    income: {
        baseIncome: number,
        increasePerYear: number,
    }
    filingStatus: TaxFilingStatus
    includeTaxes: boolean
}

export interface Portfolio {
    years: PortfolioYear[],
    parameters: PortfolioParameters;
}

const getId = () => {
    //Can change 7 to 2 for longer results.
    return (Math.random() + 1).toString(36).substring(7);
}

interface PortfolioCreateState {
    overflowBankAccount: number;
}

export const createPortfolio = (
    investPerYear: number,
    cocReturn: number,
    equityMultiple: number,
    stopInvestingAtYear: number = Infinity,
    stopReinvestingAtYear: number = Infinity,
    comparison: ComparisonInvestment,
    income: {
        baseIncome: number,
        increasePerYear: number,
    },
    filingStatus: TaxFilingStatus = TAX_FILING_SINGLE,
    includeTaxes = true,
) => {
    const AAR = (equityMultiple - 1) / YEARS_TO_MATURITY;
    const appreciation = (AAR - cocReturn) * YEARS_TO_MATURITY;

    const portfolioYears: PortfolioYear[] = [];

    const state: PortfolioCreateState = {
        overflowBankAccount: 0,
    };

    const getTotalEquity = (syndications: Syndication[]) => {
        return syndications.reduce((total, current) => total + current.amountInvested, 0);
    };

    const getThisYearInvest = (curentYear: number): number => {
        return curentYear + 1 < stopInvestingAtYear ? investPerYear : 0;
    }

    const getLastYearSyndicationTaxes = (yearIndex: number) => {
        const lastYear = portfolioYears[yearIndex - 1];
        if (!lastYear) {
            return 0;
        }
        return lastYear.taxes.getTotalSyndicationTaxes();
    }

    const shouldReinvestDistributions = (year: number) => stopReinvestingAtYear > year + 1;

    for (let y = STARTING_YEAR, i = 0; y < STARTING_YEAR + YEARS; y++, i++) {
        const yearOneBasedIdx = i + 1;
        const thisYearActiveIncome = income.baseIncome + income.increasePerYear * y;
        /* - - - - - - - - - - - - - - - - -
                CASE 1: First year
        - - - - - - - - - - - - - - - - - */
        if (i === 0) {
            const thisYearInvest = getThisYearInvest(i);
            const thisYearDistributions = getThisYearInvest(i) * cocReturn;

            const totalDepreciation = calculateDepreciationForYear(0, thisYearInvest);
            const taxableDistributions = Math.max(0, thisYearDistributions - totalDepreciation);

            const taxes = includeTaxes ? calculateTaxWithStandardDeduction(
                thisYearActiveIncome,
                { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                taxableDistributions,
                filingStatus,
            ) : calculateTaxWithStandardDeduction(
                thisYearActiveIncome,
                { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                0,
                filingStatus,
            );

            const syndications = getSyndicationsWithCarriedLoss([
                {
                    id: getId(),
                    startYear: y,
                    amountInvested: thisYearInvest,
                    totalDepreciation,
                }
            ], cocReturn, y);

            // Create the first year's portfolio entry
            const firstYearEntry: PortfolioYear = {
                year: y,
                yearOneBasedIdx,
                syndications,
                totalEquity: thisYearInvest,
                passiveIncome: taxableDistributions,
                invested: thisYearInvest > 0,
                distributions: thisYearDistributions,
                reinvested: shouldReinvestDistributions(i),
                taxes,
                reinvestFromLastYear: 0,
            };

            let thisYearPassiveIncome = thisYearDistributions;

            if (shouldReinvestDistributions(i)) {
                state.overflowBankAccount += thisYearPassiveIncome;
                thisYearPassiveIncome = 0;
            }

            // Update the passive income in the entry
            firstYearEntry.passiveIncome = thisYearPassiveIncome;

            portfolioYears.push(firstYearEntry);
        } else {
            // look for any syndications that have matured
            const lastYear: PortfolioYear | undefined = portfolioYears[i - 1];
            const reinvestFromLastYear = state.overflowBankAccount;
            state.overflowBankAccount = 0;

            /* - - - - - - - - - - - - - - - - -
                CASE 2: years 2-5
            - - - - - - - - - - - - - - - - - */
            if (y - STARTING_YEAR < YEARS_TO_MATURITY) {
                // Calculate depreciation for existing syndications
                const existingSyndications = lastYear.syndications.map(syn => {
                    const depreciation = calculateDepreciationForYear(y - syn.startYear, syn.amountInvested);

                    return {
                        ...syn,
                        totalDepreciation: syn.totalDepreciation + depreciation,
                    }
                });

                // Create new syndication
                const lastYearSyndicationTaxes = SHOULD_PAY_TAXES_WITH_EQUITY ? getLastYearSyndicationTaxes(i) : 0;
                const newInvestment = Math.max(0, getThisYearInvest(i) + reinvestFromLastYear - lastYearSyndicationTaxes);

                // Create all syndications
                const syndications: Syndication[] = getSyndicationsWithCarriedLoss([
                    ...existingSyndications,
                    ...(newInvestment > 0 ? [{
                        id: getId(),
                        startYear: y,
                        amountInvested: newInvestment,
                        totalDepreciation: calculateDepreciationForYear(0, newInvestment),
                    }] : []),
                ], cocReturn, y);
                const totalEquity = getTotalEquity(syndications);
                // step 1: get distributions
                const thisYearDistributions = calculateYearlyDistributionsForSyndications(syndications, cocReturn);

                // step 2: subtract last year's taxes
                const lastYearTaxes = getLastYearSyndicationTaxes(i);
                const leftoverTaxesAfterSubtracting = Math.max(0, lastYearTaxes - thisYearDistributions);
                const thisYearDistributionsAfterTaxes = Math.max(0, thisYearDistributions - lastYearTaxes);

                let passiveIncome = thisYearDistributionsAfterTaxes;

                if (shouldReinvestDistributions(i)) {
                    state.overflowBankAccount += passiveIncome;
                    passiveIncome = 0;
                }

                const netGain = calculateNetGainForSyndicationsThisYear(syndications, cocReturn, y, 0);

                const taxes = includeTaxes ? calculateTaxWithStandardDeduction(
                    thisYearActiveIncome,
                    { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                    Math.max(0, netGain),
                    filingStatus,
                ) : calculateTaxWithStandardDeduction(
                    thisYearActiveIncome,
                    { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                    0,
                    filingStatus,
                );

                portfolioYears.push({
                    year: y,
                    yearOneBasedIdx,
                    syndications,
                    totalEquity,
                    passiveIncome,
                    distributions: thisYearDistributions,
                    invested: getThisYearInvest(i) > 0,
                    reinvested: shouldReinvestDistributions(i),
                    taxes,
                    leftoverTaxesFromLastYear: leftoverTaxesAfterSubtracting,
                    reinvestFromLastYear,
                });
                continue;
            }

            /* - - - - - - - - - - - - - - - - -
                CASE 3: years 6-20
            - - - - - - - - - - - - - - - - - */
            if (!lastYear) {
                continue;
            }
            const active: Syndication[] = [];
            const matured: Syndication[] = [];
            lastYear.syndications.forEach((syn) => {
                if (y - syn.startYear >= YEARS_TO_MATURITY) {
                    matured.push(syn);
                } else {
                    const depreciation = calculateDepreciationForYear(y - syn.startYear, syn.amountInvested);
                    active.push({
                        ...syn,
                        totalDepreciation: syn.totalDepreciation + depreciation,
                    });
                }
            });

            const reinvestDivisor = matured.length;

            const newSyndications = matured.map((old) => {
                const amount = (old.amountInvested * (1 + appreciation)) + getThisYearInvest(i) + (reinvestFromLastYear / reinvestDivisor);
                const depreciation = calculateDepreciationForYear(0, amount);

                return {
                    id: getId(),
                    amountInvested: amount,
                    startYear: y,
                    totalDepreciation: depreciation,
                };
            });

            const syndications = [
                ...getSyndicationsWithCarriedLoss(active, cocReturn, y),
                ...getSyndicationsWithCarriedLoss(newSyndications, cocReturn, y),
            ]

            const totalEquity = getTotalEquity(syndications);
            // step 1: get distributions
            const thisYearDistributions = calculateYearlyDistributionsForSyndications(syndications, cocReturn);
            // step 2: subtract last year's taxes
            const lastYearTaxes = getLastYearSyndicationTaxes(i);
            const leftoverTaxesAfterSubtracting = Math.max(0, lastYearTaxes - thisYearDistributions);
            const thisYearDistributionsAfterTaxes = Math.max(0, thisYearDistributions - lastYearTaxes);

            const netGain = calculateNetGainForSyndicationsThisYear(syndications, cocReturn, y, 0);
            let passiveIncome = thisYearDistributionsAfterTaxes;

            if (shouldReinvestDistributions(i)) {
                state.overflowBankAccount += passiveIncome;
                passiveIncome = 0;
            }

            // calculate total taxes for the year
            const gains = matured.reduce((acc, syn) => {
                const totalDepreciation = calculateTotalDepreciation(syn, y);
                const synGains = calculateTaxableGainsFromSyndication(YEARS_TO_MATURITY, totalDepreciation, syn.amountInvested, syn.amountInvested * (1 + appreciation), syn.carriedLoss || 0);
                return {
                    depreciationRecapture: acc.depreciationRecapture + synGains.depreciationRecapture,
                    capitalGain: acc.capitalGain + synGains.capitalGain,
                    suspendedPassiveLoss: acc.suspendedPassiveLoss + synGains.suspendedPassiveLoss,
                };
            }, { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 });

            const taxes = includeTaxes ? calculateTaxWithStandardDeduction(
                thisYearActiveIncome,
                gains,
                Math.max(0, netGain),
                filingStatus,
            ) : calculateTaxWithStandardDeduction(
                thisYearActiveIncome,
                { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                0,
                filingStatus,
            );

            portfolioYears.push({
                year: y,
                yearOneBasedIdx,
                syndications,
                totalEquity,
                passiveIncome,
                distributions: thisYearDistributions,
                invested: getThisYearInvest(i) > 0,
                reinvested: shouldReinvestDistributions(i),
                taxes,
                leftoverTaxesFromLastYear: leftoverTaxesAfterSubtracting,
                reinvestFromLastYear,
            });
        }
    }

    return {
        years: portfolioYears,
        parameters: {
            investPerYear,
            cocReturn,
            equityMultiple,
            stopInvestingAtYear,
            stopReinvestingAtYear,
            comparisonInvestment: comparison,
            income,
            filingStatus,
            includeTaxes,
        },
    };
};

// TODO: Subtract taxes from equity/passive income
export const createLiquidationPortfolio = (
    investPerYear: number,
    yearlyReturn: number,
    liquidationRate: number,
    stopInvestingAtYear: number = Infinity,
    stopReinvestingDistributionsAtYear: number = Infinity,
    income: {
        baseIncome: number,
        increasePerYear: number,
    },
    filingStatus: TaxFilingStatus = TAX_FILING_SINGLE,
) => {
    const getThisYearInvestment = (currentYear: number) => currentYear + 1 < stopInvestingAtYear ? investPerYear : 0;

    const years: PortfolioYear[] = []
    for (let i = 0; i < YEARS; i++) {
        const thisYearActiveIncome = income.baseIncome + income.increasePerYear * i;
        if (i === 0) {
            const taxes = calculateTaxWithStandardDeduction(
                thisYearActiveIncome,
                { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
                0,
                filingStatus,
            );

            // passive income needs to be 0 because we're always liquidating the gains from last year
            years.push(
                {
                    year: STARTING_YEAR,
                    yearOneBasedIdx: 1,
                    totalEquity: investPerYear,
                    passiveIncome: 0,
                    syndications: [],
                    taxes,
                    reinvestFromLastYear: 0,
                }
            )
        } else {
            const lastYear = years[i - 1];
            if (lastYear?.totalEquity) {
                let totalFromLastYear = lastYear.totalEquity * (1 + yearlyReturn);
                let passiveIncome = 0;

                // if we're not reinvesting distributions, take 4% from last year
                if (i + 1 >= stopReinvestingDistributionsAtYear) {
                    passiveIncome = totalFromLastYear * liquidationRate;
                    totalFromLastYear -= passiveIncome;
                }

                const taxes = calculateTaxWithStandardDeduction(
                    thisYearActiveIncome,
                    { depreciationRecapture: 0, capitalGain: passiveIncome, suspendedPassiveLoss: 0 },
                    0,
                    filingStatus,
                );

                const totalEquity = totalFromLastYear + getThisYearInvestment(i);

                years.push(
                    {
                        year: STARTING_YEAR + i,
                        yearOneBasedIdx: i + 1,
                        totalEquity,
                        passiveIncome,
                        syndications: [],
                        taxes,
                        reinvestFromLastYear: 0,
                    }
                )
            }
        }
    }

    return years;
}

type Lot = {
    shares: number;
    price: number;
    year: number;
};

export const createLiquidationPortfolioV2 = (
    investPerYear: number,
    growthRate: number,
    liquidationRate: number,
    stopInvestingAtYear: number = Infinity,
    stopReinvestingDistributionsAtYear: number = Infinity,
    income: {
        baseIncome: number,
        increasePerYear: number,
    },
    filingStatus: TaxFilingStatus = TAX_FILING_SINGLE,
    includeTaxes = true,
) => {
    const portfolioYears: PortfolioYear[] = []

    const startingPrice = 100;
    const firstBuyYear = STARTING_YEAR;
    const lastBuyYear = STARTING_YEAR + stopInvestingAtYear - 2;
    const endYear = STARTING_YEAR + YEARS - 1;
    const startLiquidationYear = STARTING_YEAR + stopReinvestingDistributionsAtYear + 1;
    
    let currentPrice = startingPrice;
    let holdings: Lot[] = [];
    
    let lastYearPortfolioValue = 0;
    let lastYearTaxes = 0;
    for (let year = firstBuyYear; year <= endYear; year++) {
        const sharesPurchased = investPerYear / currentPrice;
        const thisYearActiveIncome = income.baseIncome + income.increasePerYear * year;

        // Step 1: Buy shares only through lastBuyYear
        if (year <= lastBuyYear) {
            holdings.push({ shares: sharesPurchased, price: currentPrice, year });
        }

        // Step 2: Calculate portfolio value
        const totalShares = holdings.reduce((sum, lot) => sum + lot.shares, 0);
        const portfolioValue = totalShares * currentPrice;

        // Step 3: Liquidate 4% starting from startLiquidationYear
        let capitalGain = 0;
        let amountToLiquidate = 0;
        
        if (year >= startLiquidationYear) {
            amountToLiquidate = lastYearPortfolioValue * liquidationRate;
            let sharesToSell = amountToLiquidate / currentPrice;

            while (sharesToSell > 0 && holdings.length > 0) {
                const lot = holdings[0];
                const sellShares = Math.min(lot.shares, sharesToSell);
                const gainPerShare = currentPrice - lot.price;
                capitalGain += sellShares * gainPerShare;

                lot.shares -= sellShares;
                sharesToSell -= sellShares;

                if (lot.shares === 0) {
                    holdings.shift();
                }
            }
            //capitalGain;
        }

        const taxes = includeTaxes ? calculateTaxWithStandardDeduction(
            thisYearActiveIncome,
            { depreciationRecapture: 0, capitalGain, suspendedPassiveLoss: 0 },
            0,
            filingStatus,
        ) : calculateTaxWithStandardDeduction(
            thisYearActiveIncome,
            { depreciationRecapture: 0, capitalGain: 0, suspendedPassiveLoss: 0 },
            0,
            filingStatus,
        );
        
        portfolioYears.push({
            year,
            yearOneBasedIdx: year - STARTING_YEAR + 1,
            totalEquity: portfolioValue,
            passiveIncome: amountToLiquidate - lastYearTaxes,
            distributions: amountToLiquidate,
            syndications: [],
            taxes,
            reinvestFromLastYear: 0,
        })
        
        // Step 4: Grow price for next year
        currentPrice *= 1 + growthRate;
        lastYearPortfolioValue = portfolioValue;
        lastYearTaxes = taxes.getTotalSyndicationTaxes();
    }

    return portfolioYears;
}


export const getEquityMultipleForSimpleYearlyReturn = (rate: number) => {
    return Math.pow((1 + rate), YEARS_TO_MATURITY);
}